import React, { useEffect, useState } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Card } from "@shared/ui/card";
import PreviewTemplate from "./preview";
import { Collapsible } from "@radix-ui/react-collapsible";
import { CollapsibleContent, CollapsibleTrigger } from "@shared/ui/collapsible";
import { ArrowLeft, ChevronDown, ChevronUp, Info } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { useNotificationChoices } from "@features/ux/hooks";
import { useGetTemplateData, useCreateTemplate, useSegmentData, useUpdateTemplate, useSignatureData } from "../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Button } from "@shared/ui/button";
import { useGetChoices } from "../../shared/hooks";

const STATUS_DRAFT = 2;
const STATUS_ACTIVE = 0;

const CreateTemplate = () => {
  const navigate = useNavigate();
  const params = useParams();
  const templateId = params.id;
  const { data: templateData } = useGetTemplateData(templateId);
  const { data: channelData } = useGetChoices("channel_choice","quick_messaging");
  const { data: audienceTypeData } = useGetChoices("audience_type","quick_messaging");
  const { data: categoryData } = useGetChoices("category");
  const { data: segmentData } = useSegmentData();
  const {data:signatureData}=useSignatureData();

  const { mutate: createTemplate,isPending: isCreating} = useCreateTemplate();
  const { mutate: updateTemplate,isPending: isUpdating} = useUpdateTemplate(templateId);
console.log("signatureee",signatureData)
  
  const [formData, setFormData] = useState({
    signatureName: "",
    content: "",
  });
  const [activeField, setActiveField] = useState<"subject" | "emailBody" | "message" | "richTextBody" | "htmlTextBody">("emailBody");

  const channelsOptions =
    channelData?.result?.map((channel) => ({
      value: channel.key,
      label: channel.value,
    })) || [];
 const segmentOptions =
  segmentData?.results?.map((segment) => ({
    value: segment.id.toString(),
    label: segment.name,
  })) || [];
const signatureOptions =
  signatureData?.results?.map((signature) => ({
    value: signature.id.toString(),
    label: signature.name,
    isDefault: signature.isDefault,
    preview: signature.preview,
  })) || [];
  const defaultSignatureId = signatureData?.results?.find(
  (signature) => signature.isDefault
)?.id?.toString() || "";
  const audienceTypeOptions =
    audienceTypeData?.result?.map((audienceType) => ({
      value: audienceType.key,
      label: audienceType.value,
    })) || [];

  const categoryOptions =
    categoryData?.result?.map((category) => ({
      value: category.key,
      label: category.value,
    })) || [];



  const isEditMode = !!params.id;
const placeholderData = [
  {
    group: "CUSTOMER",
    items: [
      { label: "First Name", value: "first_name" },
      { label: "Last Name", value: "last_name" },
      { label: "Full Name", value: "full_name" },
      { label: "Account Number", value: "account_number" },
      { label: "Email", value: "email" },
      { label: "Phone Number", value: "phone_number" },
      { label: "Category", value: "category" },
      { label: "Sub Category", value: "sub_category" },
      { label: "Billing Address", value: "billing_address" },
      { label: "Service Address", value: "service_address" },
      { label: "Plan Name", value: "plan_name" },
    ],
  },

  {
    group: "METER",
    items: [
      { label: "Meter Number", value: "meter_number" },
      { label: "Meter Type", value: "meter_type" },
      { label: "Device Number", value: "device_number" },
      { label: "Installation Date", value: "installation_date" },
      { label: "Previous Reading", value: "previous_reading" },
      { label: "Previous Reading Date", value: "previous_reading_date" },
      { label: "Current Reading", value: "current_reading" },
      { label: "Current Reading Date", value: "current_reading_date" },
      { label: "Consumption Units", value: "consumption_units" },
    ],
  },

  {
    group: "BILLING",
    items: [
      { label: "Invoice Number", value: "invoice_number" },
      { label: "Billing Cycle", value: "billing_cycle" },
      { label: "Billing Period Start Date", value: "billing_period_start_date" },
      { label: "Billing Period End Date", value: "billing_period_end_date" },
      { label: "Bill Amount", value: "bill_amount" },
      { label: "Due Date", value: "due_date" },
      { label: "Late Fee Amount", value: "late_fee_amount" },
      { label: "Disconnection Date", value: "disconnection_date" },
    ],
  },

  {
    group: "PAYMENT",
    items: [
      { label: "Payment ID", value: "payment_id" },
      { label: "Payment Date", value: "payment_date" },
      { label: "Payment Channel", value: "payment_channel" },
      { label: "Payment Mode", value: "payment_mode" },
      { label: "Payment Amount", value: "payment_amount" },
      { label: "Remaining Balance", value: "remaining_balance" },
      { label: "Receipt Number", value: "receipt_number" },
    ],
  },

  {
    group: "SERVICE ORDER",
    items: [
      { label: "Service Order ID", value: "service_order_id" },
      { label: "Service Type", value: "service_type" },
      { label: "Requested Date", value: "requested_date" },
      { label: "Requested Time", value: "requested_time" },
      { label: "Technician Name", value: "technician_name" },
      { label: "Status", value: "status" },
    ],
  },

  {
    group: "SELF SERVICE SUPPORT",
    items: [
      { label: "Service Center Name", value: "service_center_name" },
      { label: "Service Center Email", value: "service_center_email" },
      { label: "Service Center Number", value: "service_center_number" },
      { label: "Service Center Location", value: "service_center_location" },
    ],
  },
];


  const showWhenCondition = (fieldName: string) => {
    return (formValues: any) => {
      const channel = formValues.channel;
      const emailFormat = formValues.emailFormat;

      switch (channel) {
        case "0":
          switch (fieldName) {
            case "emailBody":
              return emailFormat === "plain_text";
            case "richTextBody":
              return emailFormat === "rich_text";
            case "htmlTextBody":
              return emailFormat === "html";
            case "emailFormat":
            case "subject":
            case "footer":
            case "signature":
            case "info":
            case "quickPlaceholders":
              return true;
            default:
              return false;
          }

        case "1":
          switch (fieldName) {
            case "info":
            case "message":
            case "quickPlaceholders":
              return true;
            default:
              return false;
          }

        default:
          return false;
      }
    };
  };

  // Initialize form first with empty fields
  const form = FormService.createForm({
    fields: [],
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      tags: [],
      category: "",
      channel: "",
      audience: "CUSTOMER",
      segment: "all",
      emailFormat: "plain_text",
      subject: "",
      emailBody: "",
      richTextBody: "",
      htmlTextBody: "",
      footer: "",
      signature: "",
      message: "",
    },
  });

  // Watch the current values from the form
  const selectedAudience = form.watch("audience");
  const selectedSignature = form.watch("signature");
  const selectedCategory = form.watch("category");

  // Find the label for audience
const audienceLabel = audienceTypeOptions.find(
  (option) => String(option.value) === String(selectedAudience)
)?.label || "";

  // Find the label for signature
  const signatureLabel = signatureOptions.find(
    (option) => option.value === selectedSignature
  )?.label || "";

  const signaturePreview = signatureOptions.find(
    (option) => option.value === selectedSignature
  )?.preview || "";

  // Find the label for category
  const categoryLabel = categoryOptions.find(
    (option) => option.value === selectedCategory
  )?.label || "";

  // Define handlePlaceholderClick at component level
  const handlePlaceholderClick = (placeholderValue: string) => {
    const placeholder = `{${placeholderValue}}`;

    switch (activeField) {
      case "subject":
        const currentSubject = form.getValues("subject") || "";
        form.setValue("subject", currentSubject + placeholder);
        break;

      case "emailBody":
        const currentBody = form.getValues("emailBody") || "";
        form.setValue("emailBody", currentBody + placeholder);
        break;

      case "richTextBody":
        const currentRichTextBody = form.getValues("richTextBody") || "";
        form.setValue("richTextBody", currentRichTextBody + placeholder);
        break;

      case "htmlTextBody":
        const currentHtmlTextBody = form.getValues("htmlTextBody") || "";
        form.setValue("htmlTextBody", currentHtmlTextBody + placeholder);
        break;

      case "message":
        const currentMessage = form.getValues("message") || "";
        form.setValue("message", currentMessage + placeholder);
        break;
    }
  };

  const formFields: FormField[] = [
    {
      name: "templateName",
      type: "text",
      label: "Template Name",
      placeholder: "e.g., Professional Casual, Support Team",
      required: true,
      disabled: isEditMode,
      helperText:
        "Choose a descriptive name that clearly identifies the purpose of this template",
      className: "col-span-2",
    },
    {
      name: "tags",
      type: "tags",
      label: "Tags",
      placeholder: "e.g., billing, urgent, monthly (comma-separated)",
      required: true,
      helperText:
        "Add tags to organize and categorize your templates (separate with commas)",
      className: "col-span-2",
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      options: categoryOptions,
      required: true,
      helperText:
        "Organize templates by category for easier management and filtering",
      className: "col-span-1",
    },
    {
      name: "channel",
      type: "select",
      label: "Channel",
      options: channelsOptions,
      required: true,
      helperText: "Select the communication channel for this template",
      className: "col-span-1",
    },
    {
      name: "audience",
      type: "select",
      label: "Audience",
      options: audienceTypeOptions,
      required: true,
      helperText: "Define who will receive communications using this template",
      className: "col-span-1",
    },
    {
      name: "segment",
      type: "select",
      label: "Segment",
      options: segmentOptions,
      required: true,
      helperText: "Link this template to a specific segment",
      className: "col-span-1",
    },
    {
      name: "emailFormat",
      type: "radio",
      label: "Email Format",
      groupColorNeeded: false,
      showWhen: showWhenCondition("emailFormat"),
      group: "Template Content",
      options: [
        { label: "Plain Text", value: "plain_text" },
        { label: "Rich Text", value: "rich_text" },
        { label: "HTML", value: "html" },
      ],
      defaultValue: "plain_text",
      required: false,
      helperText: "Choose between plain text, rich text editor, or raw HTML",
      className: "col-span-2 mt-2",
    },
    {
      name: "subject",
      type: "text-with-placeholder",
      label: "Subject test",
      placeholder: "Enter email subject (use {placeholders})",
      placeholders: placeholderData,
      showWhen: showWhenCondition("subject"),
      required: true,
      groupColorNeeded: false,
      group: "Template Content",
      helperText:
        "Write a clear subject line. Use placeholders like {customer_name} for personalization",
      className: "col-span-2",
    },
    {
      name: "info",
      type: "custom",
      label: "",
      required: false,
      className: "col-span-2 mt-2",
      groupColorNeeded: false,
      group: "Template Content",
      showWhen: showWhenCondition("info"),
      customRender: () => {
        const [isOpen, setIsOpen] = useState(false);
        return (
          <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className="overflow-hidden border border-blue-300 rounded-lg bg-blue-50 px-6 py-4"
          >
            <CollapsibleTrigger asChild>
              <div className="flex justify-between items-center cursor-pointer -mx-6 px-6 py-2 rounded">
                <div className="flex items-center gap-3">
                  <Info
                    size={18}
                    className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0"
                  />
                  <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                    Using Placeholders for Dynamic Content
                  </span>
                </div>
                {isOpen ? (
                  <ChevronUp size={18} className="text-blue-600" />
                ) : (
                  <ChevronDown size={18} className="text-blue-600" />
                )}
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="pt-3">
                <div className="text-xs text-blue-800 dark:text-blue-200 space-y-3">
                  <p>
                    Placeholders allow you to personalize messages with dynamic
                    data. Use the format{" "}
                    <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                      {"{{placeholder_name}}"}
                    </code>{" "}
                    in your subject and message body.
                  </p>

                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Click "Insert Placeholder" to browse available
                        placeholders by category
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Placeholders will be highlighted in your message for
                        easy identification
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Use "Test Send" to preview how placeholders are replaced
                        with sample data
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-blue-600 mt-1">•</span>
                      <span>
                        Common examples:{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{customer_name}}"}
                        </code>
                        ,{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{bill_amount}}"}
                        </code>
                        ,{" "}
                        <code className="bg-gray-200 px-1.5 py-0.5 rounded text-xs font-mono">
                          {"{{due_date}}"}
                        </code>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      },
    },
    {
      name: "emailBody",
      type: "textarea-with-placeholder",
      label: "",
      placeholder: "Enter email body. Use placeholders for dynamic content",
      placeholders: placeholderData,
      required: false,
      rows: 8,
      showWhen: showWhenCondition("emailBody"),
      groupColorNeeded: false,
      group: "Template Content",
      helperText:
        "Write your message content. Click 'Insert Placeholder' to add dynamic fields",
      className: "col-span-2",
    },
    {
      name: "richTextBody",
      type: "rich-text",
      label: "Email Body (Rich Text)",
      placeholder: "Compose your email...",
      placeholderNeeded: true,
      placeholders: placeholderData,
      showWhen: showWhenCondition("richTextBody"),
      groupColorNeeded: false,
      group: "Template Content",
      helperText:
        "Use the toolbar to format your email. Placeholders can be inserted using the buttons below",
      required: false,
      className: "col-span-2",
    },
    {
      name: "htmlTextBody",
      type: "textarea",
      label: "Email Body (HTML Text)",
      placeholder: "Compose your email...",
      placeholderNeeded: true,
      placeholders: placeholderData,
      showWhen: showWhenCondition("htmlTextBody"),
      groupColorNeeded: false,
      group: "Template Content",
      helperText: "Write raw HTML code. Use placeholders for dynamic content",
      required: false,
      className: "col-span-2",
    },
    {
      name: "footer",
      type: "textarea",
      label: "Footer",
      groupColorNeeded: false,
      group: "Template Content",
      showWhen: showWhenCondition("footer"),
      required: false,
      helperText: "This content will appear at the bottom of your email",
      className: "col-span-2",
    },
    {
      name: "signature",
      type: "select",
      label: "Signature",
      options: signatureOptions,
      groupColorNeeded: false,
      group: "Template Content",
       defaultValue:defaultSignatureId,
      showWhen: showWhenCondition("signature"),
      required: false,
      helperText: "This content will appear at the bottom of your email",
      className: "col-span-2",
    },
    {
      name: "message",
      type: "textarea-with-placeholder",
      label: "",
      placeholder: "Enter message (use {placeholders})",
      placeholders: placeholderData,
      showWhen: showWhenCondition("message"),
      required: true,
      groupColorNeeded: false,
      group: "Template Content",
      helperText:
        "Write your message. Use placeholders like {customer_name} for personalization",
      className: "col-span-2",
    },
    {
      name: "quickPlaceholders",
      type: "custom",
      label: "",
      required: false,
      className: "col-span-2",
      showWhen: showWhenCondition("quickPlaceholders"),
      groupColorNeeded: false,
      group: "Template Content",
      customRender: () => {
        const channelValue = form.watch("channel");
        const emailFormatValue = form.watch("emailFormat");

        return (
          <div className="border border-gray-300 rounded-lg bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700">
                Quick Placeholders
              </h4>
              <div className="flex gap-2">
                {channelValue === "0" && (
                  <>
                    <button
                      type="button"
                      onClick={() => setActiveField("subject")}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                        activeField === "subject"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      Subject
                    </button>
                    {emailFormatValue === "0" && (
                      <button
                        type="button"
                        onClick={() => setActiveField("emailBody")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          activeField === "emailBody"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Email Body
                      </button>
                    )}
                    {emailFormatValue === "1" && (
                      <button
                        type="button"
                        onClick={() => setActiveField("richTextBody")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          activeField === "richTextBody"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        Rich Text Body
                      </button>
                    )}
                    {emailFormatValue === "2" && (
                      <button
                        type="button"
                        onClick={() => setActiveField("htmlTextBody")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          activeField === "htmlTextBody"
                            ? "bg-blue-600 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        HTML Body
                      </button>
                    )}
                  </>
                )}
                {channelValue === "1" && (
                  <button
                    type="button"
                    onClick={() => setActiveField("message")}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      activeField === "message"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    Message
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto space-y-4">
              {placeholderData.map((group) => (
                <div key={group.group}>
                  <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    {group.group}
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {group.items.map((item) => (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => handlePlaceholderClick(item.value)}
                        className="px-3 py-1.5 text-xs font-medium bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      },
    },
  ];

useEffect(() => {
  if (isEditMode && templateData) {
    form.reset({
      templateName: templateData?.templateName || "",
      tags: templateData?.tags || [],
      category: templateData?.templateCategory || "",
      channel: templateData?.channel || "",
      audience: templateData?.audienceType,
      segment: templateData?.segment,
      emailFormat: templateData?.emailFormat,
      subject: templateData?.content?.subject || "",
      emailBody: templateData?.content?.body || "",
      richTextBody: templateData?.content?.body || "",
      htmlTextBody: templateData?.content?.body || "",
      footer: templateData?.content?.footer,
      signature: templateData?.signature,
      message: templateData?.content?.body || "",
    });
  } else {
    form.setValue("emailFormat", "plain_text");
  }
}, [templateData, isEditMode, form]);
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

const handleSubmit = async () => {
  const values = form.getValues();
  
  console.log("Submitting template:", values);

  // Determine the body content based on channel and format
  let bodyContent = "";
  let emailFormat = null;
  
  if (values.channel === "0") {
    // Email channel
    if (values.emailFormat === "plain_text") {
      bodyContent = values.emailBody || "";
      emailFormat = "plain_text";
    } else if (values.emailFormat === "rich_text") {
      bodyContent = values.richTextBody || "";
      emailFormat = "rich_text";
    } else if (values.emailFormat === "html") {
      bodyContent = values.htmlTextBody || "";
      emailFormat = "html";
    }
  } else if (values.channel === "1") {
    // Text message channel
    bodyContent = values.message || "";
  }

  // Prepare the payload according to API spec
  const payload = {
    remote_utility_id: getRemoteUtilityId(),
    template_name: values.templateName,
    description: "",
    channel: parseInt(values.channel),
    email_format: emailFormat,
    audience_type: values.audience && values.audience !== "all" ? parseInt(values.audience) : null,
    segment: values.segment && values.segment !== "all" ? parseInt(values.segment) : null,
    signature: values.signature ? parseInt(values.signature) : (defaultSignatureId ? parseInt(defaultSignatureId) : null),
    content: {
      subject: values.subject || "",
      body: bodyContent,
      footer: values.footer || ""
    },
    tags: Array.isArray(values.tags) ? values.tags : [],
    template_category: values.category || "",
    status: STATUS_ACTIVE, // Add active status
  };

  console.log("API Payload:", payload);

  // Check if edit mode or create mode
  if (isEditMode) {
    // Call update API
    updateTemplate(payload, {
      onSuccess: () => {
        console.log("Template updated successfully");
        navigate("/communication-hub/templates");
      },
    });
  } else {
    // Call create API
    createTemplate(payload, {
      onSuccess: () => {
        console.log("Template created successfully");
        navigate("/communication-hub/templates");
      },
    });
  }
};

const handleSaveDraft = async () => {
  const values = form.getValues();
  console.log("Saving template as draft:", values);

  // Determine the body content based on channel and format
  let bodyContent = "";
  let emailFormat = null;
  
  if (values.channel === "0") {
    // Email channel
    if (values.emailFormat === "plain_text") {
      bodyContent = values.emailBody || "";
      emailFormat = "plain_text";
    } else if (values.emailFormat === "rich_text") {
      bodyContent = values.richTextBody || "";
      emailFormat = "rich_text";
    } else if (values.emailFormat === "html") {
      bodyContent = values.htmlTextBody || "";
      emailFormat = "html";
    }
  } else if (values.channel === "1") {
    // Text message channel
    bodyContent = values.message || "";
  }

  // Prepare the payload according to API spec
  const payload = {
    remote_utility_id: getRemoteUtilityId(),
    template_name: values.templateName,
    description: "",
    channel: parseInt(values.channel),
    email_format: emailFormat,
    audience_type: values.audience && values.audience !== "all" ? parseInt(values.audience) : null,
    segment: values.segment && values.segment !== "all" ? parseInt(values.segment) : null,
    signature: values.signature ? parseInt(values.signature) : (defaultSignatureId ? parseInt(defaultSignatureId) : null),
    content: {
      subject: values.subject || "",
      body: bodyContent,
      footer: values.footer || ""
    },
    tags: Array.isArray(values.tags) ? values.tags : [],
    template_category: values.category || "",
    status: STATUS_DRAFT, // Add draft status
  };

  console.log("API Payload (Draft):", payload);

  // Check if edit mode or create mode
  if (isEditMode) {
    // Call update API
    updateTemplate(payload, {
      onSuccess: () => {
        console.log("Template draft saved successfully");
        navigate("/communication-hub/templates");
      },
    });
  } else {
    // Call create API
    createTemplate(payload, {
      onSuccess: () => {
        console.log("Template draft created successfully");
        navigate("/communication-hub/templates");
      },
    });
  }
};

  const handleCancel = () => {
    form.reset();
    navigate("/communication-hub/templates");
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <ArrowLeft
          className="w-5 h-5 cursor-pointer text-gray-700 hover:text-gray-900"
          onClick={() => navigate("/communication-hub/templates")}
        />
        <div className="flex flex-col">
          <PageHeader
            title={isEditMode ? "Edit Template" : "Create Template"}
            subtitle="Configure fields and content for your template"
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-6">
          <h2 className="text-lg font-bold tracking-tight mb-3">
            Template Configuration
          </h2>
          <DynamicForm
            fields={formFields}
            form={form}
            gridColumns={2}
            responsiveColumns={{ sm: 1, md: 2 }}
            onSubmit={handleSubmit}
            onReset={handleCancel}
            
            onFieldChange={handleFieldChange}
            config={{
              resetButtonText: "Cancel",
              submitButtonText: isEditMode ? "Update Template" : "Save Template",
              
              layout: "grid",
              columns: 2,
              showSubmitButton: false, // Hide default submit button
              showResetButton: false,
              spacing: "medium",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700",
                buttonContainer: "flex justify-end gap-2",
                input:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
              },
            }}
          />
          {/* Custom Button Container */}
          <div className="flex justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleSaveDraft}
              
            >
              Save as Draft
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
            >
              {isEditMode ? "Update Template" : "Save Template"}
            </Button>
          </div>
        </Card>
        <Card className="col-span-6">
          <PreviewTemplate 
            form={form} 
            audienceLabel={audienceLabel}
            signatureLabel={signaturePreview}
            categoryLabel={categoryLabel}
          />
        </Card>
      </div>
    </div>
  );
};

export default CreateTemplate;